import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { toast } from "sonner";
import { ArrowLeft, Shield, Users, Save, Trash2, ShieldCheck, ShieldPlus, Link2, Copy, RefreshCw, UserCheck, Check, X, Clock } from "lucide-react";

export default function GroupManage() {
  const { groupId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [admins, setAdmins] = useState([]);
  const [joinRequests, setJoinRequests] = useState([]);
  const [form, setForm] = useState({ name: "", description: "", color: "#FF3B30", visibility: "public" });
  const [busy, setBusy] = useState(false);

  const isAppAdmin = user?.role === "admin";
  const canAdmin = isAppAdmin || (user?.group_admin_ids || []).includes(groupId);

  const load = useCallback(async () => {
    const [pRes, aRes, jRes] = await Promise.all([
      api.get(`/groups/${groupId}`),
      api.get(`/groups/${groupId}/admins`),
      api.get(`/groups/${groupId}/join-requests`),
    ]);
    setProfile(pRes.data);
    setAdmins(aRes.data);
    setJoinRequests(jRes.data);
    setForm({ name: pRes.data.name || "", description: pRes.data.description || "", color: pRes.data.color || "#FF3B30", visibility: pRes.data.visibility || "public" });
  }, [groupId]);

  useEffect(() => {
    if (!canAdmin) {
      navigate(`/groups/${groupId}`, { replace: true });
      return;
    }
    load().catch((err) => { console.error("GroupManage load failed:", err); toast.error("Failed to load group"); });
  }, [groupId, canAdmin, load, navigate]);

  if (!profile) return null;
  const adminIds = new Set(admins.map((a) => a.user_id));

  const saveMeta = async () => {
    setBusy(true);
    try {
      await api.patch(`/groups/${groupId}`, form);
      toast.success("Group updated");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const makeAdmin = async (uid) => {
    try {
      await api.post(`/groups/${groupId}/admins`, { user_id: uid });
      toast.success("Group admin added");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const removeAdmin = async (uid) => {
    try {
      await api.delete(`/groups/${groupId}/admins/${uid}`);
      toast.success("Group admin removed");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const removeMember = async (uid, name) => {
    if (!window.confirm(`Remove ${name} from ${profile.name}?`)) return;
    try {
      await api.delete(`/groups/${groupId}/members/${uid}`);
      toast.success("Member removed");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const approveRequest = async (rid, name) => {
    try {
      await api.post(`/groups/${groupId}/join-requests/${rid}/approve`);
      toast.success(`${name} added to ${profile.name}`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const rejectRequest = async (rid) => {
    try {
      await api.post(`/groups/${groupId}/join-requests/${rid}/reject`);
      toast.success("Request declined");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const slugify = (s) => (s || "").toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
  const inviteSlug = [slugify(profile.tournament_name), slugify(profile.name)].filter(Boolean).join("-");
  const inviteUrl = profile.join_code
    ? `${window.location.origin}/join/${profile.join_code}${inviteSlug ? `/${inviteSlug}` : ""}`
    : "";

  const copyInvite = async () => {
    try {
      await navigator.clipboard.writeText(inviteUrl);
      toast.success("Invite link copied");
    } catch { toast.error("Couldn't copy — select and copy manually"); }
  };

  const regenerate = async () => {
    try {
      const { data } = await api.post(`/groups/${groupId}/invite-code`);
      setProfile((p) => ({ ...p, join_code: data.join_code }));
      toast.success("New invite code generated — the old link no longer works");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="px-3 md:px-8 py-6 md:py-8 grid-bg min-h-[calc(100vh-65px)] max-w-5xl mx-auto" data-testid="group-manage">
      <Link to={`/groups/${groupId}`} className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-zinc-400 hover:text-white mb-4" data-testid="back-to-group">
        <ArrowLeft size={14}/> Back to {profile.name}
      </Link>

      <div className="flex items-center gap-3 mb-6">
        <div className="w-9 h-9 bg-primary-accent flex items-center justify-center"><Shield size={18} /></div>
        <div>
          <div className="label-tag">{isAppAdmin ? "App Admin" : "Group Admin"} · Management</div>
          <h1 className="font-display text-3xl md:text-4xl uppercase">Manage {profile.name}</h1>
        </div>
      </div>

      {/* EDIT META */}
      <div className="surface-card p-5 mb-6" data-testid="group-edit-meta">
        <h2 className="font-display text-xl uppercase mb-4">Group Details</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <div className="label-tag mb-1">Name</div>
            <input data-testid="edit-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="w-full bg-zinc-950 border border-zinc-800 px-3 py-2 focus:border-primary-accent focus:outline-none" />
          </div>
          <div>
            <div className="label-tag mb-1">Accent Color</div>
            <div className="flex items-center gap-2">
              <input data-testid="edit-color-input" type="color" value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })}
                className="w-12 h-9 bg-zinc-950 border border-zinc-800" />
              <span className="font-display text-sm">{form.color}</span>
            </div>
          </div>
          <div className="md:col-span-2">
            <div className="label-tag mb-1">Description</div>
            <input data-testid="edit-desc-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
              className="w-full bg-zinc-950 border border-zinc-800 px-3 py-2 focus:border-primary-accent focus:outline-none" />
          </div>
          <div className="md:col-span-2">
            <div className="label-tag mb-1">Visibility</div>
            <select data-testid="edit-visibility-input" value={form.visibility} onChange={(e) => setForm({ ...form, visibility: e.target.value })}
              className="w-full bg-zinc-950 border border-zinc-800 px-3 py-2 focus:border-primary-accent focus:outline-none">
              <option value="public">Public — anyone can join from the group list</option>
              <option value="invite_only">Invite-only — joinable only via the invite link</option>
            </select>
          </div>
        </div>
        <button data-testid="save-meta-btn" onClick={saveMeta} disabled={busy}
          className="mt-4 bg-primary-accent text-white px-5 py-2.5 text-xs uppercase tracking-widest flex items-center gap-2 hover:bg-[var(--primary-hover)] disabled:opacity-50">
          <Save size={14}/> {busy ? "Saving…" : "Save Details"}
        </button>
      </div>

      {/* INVITE LINK */}
      <div className="surface-card p-5 mb-6" data-testid="group-invite">
        <div className="flex items-center gap-2 mb-2">
          <Link2 size={18} className="text-primary-accent" />
          <h2 className="font-display text-xl uppercase">Invite Link</h2>
        </div>
        <p className="text-sm text-zinc-400 mb-4">Share this one-tap link to recruit members straight into {profile.name}.</p>
        <div className="flex flex-col sm:flex-row gap-2">
          <div className="flex-1 bg-zinc-950 border border-zinc-800 px-3 py-2 font-mono text-sm text-zinc-200 overflow-x-auto whitespace-nowrap" data-testid="invite-link-value">
            {inviteUrl || "—"}
          </div>
          <button data-testid="copy-invite-btn" onClick={copyInvite}
            className="bg-primary-accent text-white px-4 py-2 text-xs uppercase tracking-widest flex items-center justify-center gap-2 hover:bg-[var(--primary-hover)]">
            <Copy size={14}/> Copy
          </button>
          <button data-testid="regenerate-invite-btn" onClick={regenerate}
            className="border border-zinc-700 text-zinc-300 px-4 py-2 text-xs uppercase tracking-widest flex items-center justify-center gap-2 hover:border-primary-accent hover:text-white">
            <RefreshCw size={14}/> New Code
          </button>
        </div>
        <div className="mt-3 text-xs text-zinc-500 uppercase tracking-widest">Code: <span className="text-primary-accent font-display tracking-[0.2em]" data-testid="invite-code-value">{profile.join_code || "—"}</span></div>
      </div>

      {/* JOIN REQUESTS */}
      <div className="surface-card p-5 mb-6" data-testid="join-requests-section">
        <div className="flex items-center gap-2 mb-1">
          <UserCheck size={18} className="text-primary-accent" />
          <h2 className="font-display text-xl uppercase">Join Requests</h2>
          {joinRequests.length > 0 && (
            <span data-testid="join-requests-count" className="ml-1 min-w-[20px] h-5 px-1.5 bg-primary-accent text-white text-[11px] font-display flex items-center justify-center rounded-full">
              {joinRequests.length}
            </span>
          )}
        </div>
        <p className="text-sm text-zinc-400 mb-4">People asking to join this invite-only group. Approve to add them as members.</p>
        {joinRequests.length === 0 ? (
          <div className="text-sm text-zinc-500 flex items-center gap-2 py-2" data-testid="join-requests-empty">
            <Clock size={14} /> No pending requests.
          </div>
        ) : (
          <div className="space-y-2">
            {joinRequests.map((r) => (
              <div key={r.request_id} data-testid={`join-request-${r.request_id}`}
                className="flex items-center justify-between gap-3 border border-white/5 px-3 py-2.5">
                <div className="min-w-0">
                  <div className="font-display tracking-wide truncate">{r.requester_name}</div>
                  {r.requester_email && <div className="text-xs text-zinc-500 truncate">{r.requester_email}</div>}
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <button data-testid={`approve-request-${r.request_id}`} onClick={() => approveRequest(r.request_id, r.requester_name)}
                    className="inline-flex items-center gap-1 bg-primary-accent text-white px-3 py-1.5 text-xs uppercase tracking-widest hover:bg-[var(--primary-hover)]">
                    <Check size={13} /> Approve
                  </button>
                  <button data-testid={`reject-request-${r.request_id}`} onClick={() => rejectRequest(r.request_id)}
                    className="inline-flex items-center gap-1 border border-zinc-700 text-zinc-300 px-3 py-1.5 text-xs uppercase tracking-widest hover:border-primary-accent hover:text-white">
                    <X size={13} /> Decline
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* MEMBERS + ADMINS */}
      <div className="surface-card overflow-x-auto" data-testid="group-members-manage">
        <div className="flex items-center gap-2 p-4 pb-2">
          <Users size={18} className="text-primary-accent" />
          <h2 className="font-display text-xl uppercase">Members &amp; Admins</h2>
        </div>
        <table className="w-full min-w-[560px]">
          <thead>
            <tr className="border-b border-white/5">
              <th className="text-left px-4 py-3 label-tag">Member</th>
              <th className="text-left px-4 py-3 label-tag">Role</th>
              <th className="text-right px-4 py-3 label-tag">Actions</th>
            </tr>
          </thead>
          <tbody>
            {(profile.members || []).length === 0 && (
              <tr><td colSpan={3} className="text-center text-zinc-500 py-8">No members yet.</td></tr>
            )}
            {(profile.members || []).map((m) => {
              const isAdmin = adminIds.has(m.user_id);
              const isSelf = m.user_id === user?.user_id;
              return (
                <tr key={m.user_id} className="border-b border-white/5" data-testid={`manage-member-${m.user_id}`}>
                  <td className="px-4 py-3 font-display text-base tracking-wide">{m.name}</td>
                  <td className="px-4 py-3">
                    {isAdmin ? (
                      <span className="inline-flex items-center gap-1 text-xs uppercase tracking-widest text-primary-accent" data-testid={`role-${m.user_id}`}>
                        <ShieldCheck size={12}/> Group Admin
                      </span>
                    ) : (
                      <span className="text-xs uppercase tracking-widest text-zinc-500" data-testid={`role-${m.user_id}`}>Participant</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right space-x-3 whitespace-nowrap">
                    {isAdmin ? (
                      <button data-testid={`remove-admin-${m.user_id}`} onClick={() => removeAdmin(m.user_id)} disabled={isSelf}
                        className="text-xs uppercase tracking-widest text-zinc-400 hover:text-white disabled:opacity-30"
                        title={isSelf ? "You can't demote yourself" : "Remove admin rights"}>
                        Remove Admin
                      </button>
                    ) : (
                      <button data-testid={`make-admin-${m.user_id}`} onClick={() => makeAdmin(m.user_id)}
                        className="inline-flex items-center gap-1 text-xs uppercase tracking-widest text-primary-accent hover:text-white">
                        <ShieldPlus size={12}/> Make Admin
                      </button>
                    )}
                    <button data-testid={`remove-member-${m.user_id}`} onClick={() => removeMember(m.user_id, m.name)} disabled={isSelf}
                      className="inline-flex items-center gap-1 text-xs uppercase tracking-widest text-primary-accent hover:text-[var(--primary-hover)] disabled:opacity-30"
                      title={isSelf ? "You can't remove yourself" : "Remove member"}>
                      <Trash2 size={12}/> Remove
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-zinc-500 mt-3">
        {isAppAdmin
          ? "As App Admin you can appoint or remove group admins for any group."
          : "As Group Admin you can manage members and appoint other group admins for this group."}
      </p>
    </div>
  );
}
